"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { usePathname } from "next/navigation";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { type Locale, translations, getTranslation } from "@/lib/i18n";
import { getLocaleFromPath } from "@/lib/locale";
interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

/**
 * Zustand store — kept for auth sync (LocaleSync) and as fallback.
 * Primary locale source is now the URL pathname.
 */
export const useLocaleStore = create<LocaleState>()(persist(set => ({
  locale: "fr",
  setLocale: locale => set({
    locale
  })
}), {
  name: "velyq-locale"
}));

/**
 * Hook to access translations.
 * Locale is derived from the URL pathname (/fr/… or /en/…).
 */
export function useTranslation() {
  const $ = _c(9);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = getLocaleFromPath(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const locale = t0 as Locale;
  const {
    setLocale
  } = useLocaleStore();
  let t1;
  if ($[2] !== locale) {
    t1 = path => getTranslation(locale, path);
    $[2] = locale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t = t1;
  const tObj = translations[locale];
  let t2;
  if ($[4] !== locale || $[5] !== setLocale || $[6] !== t || $[7] !== tObj) {
    t2 = {
      t,
      tObj,
      locale,
      setLocale
    };
    $[4] = locale;
    $[5] = setLocale;
    $[6] = t;
    $[7] = tObj;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/**
 * Get translation object for a specific section
 */
export function useSection(section) {
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname) as Locale;
  return translations[locale][section];
}